import React, { PropsWithChildren, useEffect } from 'react';
import { connect, FormikActions, FormikProps } from 'formik';
import Observer from 'web-shared/services/observer';
import { FormActions } from './types/FormActions';

export interface FormStateProviderContextValue<FormValuesT> {
  actions: FormActions<FormValuesT>;
  getState: () => FormikProps<FormValuesT>;
  observer: Observer<FormikProps<FormValuesT>>;
}

export const FormStateContext = React.createContext<FormStateProviderContextValue<any> | null>(null);

const FormStateProvider = connect<PropsWithChildren>(({ formik, children }) => {
  const formikChangeObserverRef = React.useRef<Observer<FormikProps<any>>>(new Observer());
  const formikStateRef = React.useRef<FormikProps<any>>(formik);

  useEffect(() => {
    formikStateRef.current = formik;
    formikChangeObserverRef.current.notify(formik);
  }, [formik]);

  const contextValue = React.useMemo(() => {
    const actions: FormikActions<any> = {
      setFieldValue: formik.setFieldValue,
      setFieldError: formik.setFieldError,
      setFieldTouched: formik.setFieldTouched,
      setValues: formik.setValues,
      validateForm: formik.validateForm,
      validateField: formik.validateField,
      resetForm: formik.resetForm,
      submitForm: formik.submitForm,
      setFormikState: formik.setFormikState,
      setError: formik.setError,
      setErrors: formik.setErrors,
      setSubmitting: formik.setSubmitting,
      setTouched: formik.setTouched,
      setStatus: formik.setStatus,
    };

    return {
      actions,
      getState: () => formikStateRef.current,
      observer: formikChangeObserverRef.current,
    };
  }, []);

  return <FormStateContext.Provider value={contextValue}>
    {children}
  </FormStateContext.Provider>;
});

export default FormStateProvider;